<template>
    <div class="article-chapter">
        <slot></slot>
        <div
            v-for="(section, index) in chapter.content"
            :key="index"
            :ref="`section-${index}`"
            class="article-section"
            :class="[`layout-${section.layout}`, animationClass(section)]"
            :style="sectionStyle(section)"
        >
            <template v-if="section.layout === 'text_image_left'">
                <div class="two-col">
                    <div class="col-text" v-html="section.text"></div>
                    <div class="col-image">
                        <img :src="section.image" alt="" class="section-image" />
                    </div>
                </div>
            </template>
            <template v-else-if="section.layout === 'text_image_right'">
                <div class="two-col">
                    <div class="col-image">
                        <img :src="section.image" alt="" class="section-image" />
                    </div>
                    <div class="col-text" v-html="section.text"></div>
                </div>
            </template>
            <template v-else-if="section.layout === 'image'">
                <div class="article-section-inner">
                    <img :src="section.image" alt="" class="section-image" />
                </div>
            </template>
            <template v-else-if="section.layout === 'two_columns'">
                <div class="two-col">
                    <div class="col-text" v-html="section.text"></div>
                    <div class="col-text" v-html="section.text_secondary"></div>
                </div>
            </template>
            <template v-else>
                <div class="article-section-inner">
                    <div v-if="section.text" v-html="section.text"></div>
                </div>
            </template>
        </div>
        <div class="article-complete-card">
            <a class="btn primary-btn" @click="$router.push({ name: 'categoryDashboard', params: { category: $route.params.category } })">{{
              $t("ARTICLE_COMPLETE_BACK_BUTTON")
            }}</a>
        </div>
    </div>
</template>
<!---------------------------------------------------------------------------->
<script>
    import { mapGetters } from "vuex";

    export default {
        name: "ArticleChapter",

        computed: {
            ...mapGetters(["chapter"]),
        },

        mounted() {
            this._observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add("is-visible");
                            this._observer.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.1 }
            );

            this.chapter.content.forEach((_, index) => {
                const refs = this.$refs[`section-${index}`];
                const el = Array.isArray(refs) ? refs[0] : refs;
                if (el) this._observer.observe(el);
            });
        },

        beforeDestroy() {
            if (this._observer) this._observer.disconnect();
        },

        methods: {
            animationClass(section) {
                if (!section.animation || section.animation === "none") return null;
                return `animate-${section.animation.replace(/_/g, "-")}`;
            },

            sectionStyle(section) {
                if (section.layout !== "full_height") return {};
                const style = {};
                if (section.background_color) style.backgroundColor = section.background_color;
                if (section.background_image) style.backgroundImage = `url(${section.background_image})`;
                return style;
            },
        },
    };
</script>
<!---------------------------------------------------------------------------->
<style lang="scss" type="text/scss">
    .article-chapter {
        width: 100%;

        p, ul {
            font-size: 22px;
            line-height: 140%;
            margin-bottom: 30px;
        }

        ul {
            padding-left: 20px;
        }

        a {
            text-decoration: underline;
        }

        h1, h2, h3 {
            padding: 10px 0;
        }

        h1 {
            font-weight: 700;
            font-size: 40px;
            line-height: 50px;
            color: #064ea1;
        }

        h2 {
            font-weight: 700;
            font-size: 32px;
            line-height: 40px;
            color: #21416c;
        }

        h3 {
            font-weight: 600;
            font-size: 22px;
            line-height: 33px;
            color: #303030;
        }

        img {
            border-radius: 20px;
        }

        .article-section {
            display: flex;
            justify-content: center;
            padding: 30px 0;

            &.layout-full_height {
                min-height: calc(100vh - 200px);
                align-items: center;
                background-size: cover;
                background-repeat: no-repeat;
                background-position: center;
            }

            &.layout-normal {
                align-items: flex-start;
            }

            &.layout-text_image_left,
            &.layout-text_image_right {
                align-items: center;
            }

            &.layout-two_columns {
                align-items: flex-start;

                .two-col {
                    align-items: flex-start;
                }
            }
        }

        .animate-slide-from-left,
        .animate-slide-from-right,
        .animate-fade-in {
            opacity: 0;
            transition: opacity 0.6s ease, transform 0.6s ease;

            &.is-visible {
                opacity: 1;
                transform: none;
            }
        }

        .animate-slide-from-left {
            transform: translateX(-60px);
        }

        .animate-slide-from-right {
            transform: translateX(60px);
        }

        .animate-fade-in {
            transform: none;
        }

        .article-section-inner {
            width: 100%;
        }

        .two-col {
            display: flex;
            width: 100%;
            max-width: 1100px;
            gap: 40px;
            align-items: center;

            .col-text,
            .col-image {
                flex: 1;
            }

            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .section-image {
            width: 100%;
            height: auto;
            display: block;
        }
    }
</style>
